import { CalendarCheck, Droplets } from "lucide-react";
import { openQuoteModal } from "./quote-modal";

const PHONE_TEL = "tel:+15189001913";
const PHONE_DISPLAY = "(518) 900-1913";

/**
 * Seasonal availability bar shown above the site header on every page except
 * the paid-traffic instant-quote landing page (which has its own offer hero).
 *
 * It is rendered INSIDE the sticky <header> (see site-header.tsx), so it stays
 * pinned directly above the "Get Quote" button instead of scrolling away.
 */
export function SeasonBanner({ className = "" }: { className?: string } = {}) {
  return (
    <div
      role="status"
      className={`w-full bg-[var(--brand-deep-blue)] text-white ${className}`}
    >
      <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-center gap-x-2.5 gap-y-1 px-3 pt-2 pb-2.5 text-center sm:px-6 sm:py-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide sm:text-[13px]">
          <CalendarCheck className="h-3.5 w-3.5 shrink-0 text-[var(--brand-red)]" aria-hidden="true" />
          We&apos;re booked for the season
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold sm:text-[13px]">
          <Droplets className="h-3.5 w-3.5 shrink-0 text-[var(--brand-red)]" aria-hidden="true" />
          Gutter cleaning still available
        </span>
        <span className="text-[11px] text-white/85 sm:text-[13px]">
          Book yours —{" "}
          <button
            type="button"
            onClick={() => openQuoteModal()}
            className="font-bold text-white underline underline-offset-2 transition-colors hover:text-[var(--brand-red)]"
          >
            get a free quote
          </button>
          <span className="hidden sm:inline">
            {" "}
            or call{" "}
            <a
              href={PHONE_TEL}
              className="font-bold text-white underline underline-offset-2 transition-colors hover:text-[var(--brand-red)]"
            >
              {PHONE_DISPLAY}
            </a>
          </span>
        </span>
      </div>
    </div>
  );
}

/**
 * Compact form of the same message for inside the quote forms and the quote
 * modal, so it sits directly beside the "Get Free Quote" action and nobody
 * submits a request for a date we already can't take.
 */
export function SeasonNotice({ className = "" }: { className?: string }) {
  return (
    <div
      role="status"
      className={
        "flex items-start gap-2 rounded-md bg-[var(--brand-deep-blue)] px-3 py-2.5 text-white " +
        className
      }
    >
      <CalendarCheck
        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand-red)]"
        aria-hidden="true"
      />
      <p className="text-[13px] font-bold leading-snug">
        We&apos;re booked for the season — gutter cleaning still available.
        <span className="mt-0.5 block text-[12px] font-medium text-white/85">
          Quotes are still free: send yours and we&apos;ll confirm the earliest
          opening we have.
        </span>
      </p>
    </div>
  );
}
